import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    IconArrowLeft, IconCheck, IconX, IconRefresh, IconPencil, IconClock,
    IconChevronLeft, IconChevronRight, IconSparkles,
} from '@tabler/icons-react';
import aiTemplateService from '../../../services/aiTemplateService';
import { useAiTemplateCacheStore } from '../../../store/aiTemplateCacheStore';
import { optimizeImage } from '../../../utils/optimizeImage';
import { isAiCapacityError } from '../../../utils/aiError';
import { slugify } from '../../../utils/slugifyBn';
import { GENDER_OPTIONS, ANALYZE_FIELD_TYPE_OPTIONS } from '../../../data/aiTemplateAnalyzeOptions';

// Hard cap on how many images one bulk run may process.
export const MAX_BULK_IMAGES = 10;

// How many images are analyzed/saved at the same time. Sequential is too slow
// for 10 vision calls; more than 2 risks provider rate limits.
const CONCURRENCY = 2;

const VALID_IMAGE_SIZES = ['auto', 'portrait_2x3', 'square_1x1', 'landscape_3x2'];

const uid = () => Math.random().toString(36).slice(2, 9);
const toKey = (label) => String(label || '')
    .toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

// Same field normalisation the single-image path applies to the analysis result.
const mapFields = (arr) => (Array.isArray(arr) ? arr : []).map(f => ({
    id:            uid(),
    label:         f.label || '',
    labelBn:       f.labelBn || '',
    key:           f.key || toKey(f.label),
    type:          f.type || 'text',
    placeholder:   f.placeholder || '',
    placeholderBn: f.placeholderBn || '',
    required:      !!f.required,
    charLimit:     f.charLimit ?? ((f.type || 'text') === 'text' ? 50 : null),
    minLength:     f.minLength ?? ((f.type || 'text') === 'text' ? 0 : null),
    minValue:      f.minValue ?? null,
    maxValue:      f.maxValue ?? null,
    defaultColor:  f.defaultColor || '#000000',
    choices:       Array.isArray(f.choices) ? f.choices : [],
    suggestions:   Array.isArray(f.suggestions) ? f.suggestions : [],
    dateAuto:      f.dateAuto !== false,
    dateLang:      ['en', 'ar'].includes(f.dateLang) ? f.dateLang : 'bn',
}));

const chipCls = (active) => `px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${active
    ? 'border-primary bg-primary/10 text-primary'
    : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`;

/**
 * Bulk path, two phases:
 *  1. configure — walk the uploaded images with Prev/Next and optionally give
 *     each one a note, audience genders and field types (same options as the
 *     single-image path). The last image shows the "generate all" button.
 *  2. run — every image is analyzed (with its own options) and saved straight
 *     away as a DRAFT template; the admin reviews drafts from the list.
 */
export default function BulkTemplateCreatePanel({ files, categories, onBack }) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [items, setItems] = useState(() => files.map(file => ({
        id: uid(),
        file,
        status: 'waiting', // waiting | analyzing | saving | done | failed
        error: null,
        templateName: null,
        createdId: null,
        // Per-image analyzer options, filled in the configure phase.
        note: '',
        genders: [],
        fieldTypes: [],
    })));
    const [phase, setPhase] = useState('configure'); // 'configure' | 'run'
    const [cfgIndex, setCfgIndex] = useState(0);
    // Slugs already used by this batch — collisions get a -2 / -3 suffix so two
    // similar photos (same AI-generated name) don't fight over one slug.
    const usedSlugsRef = useRef(new Set());
    const startedRef = useRef(false);

    // Blob previews are created in an effect — NOT the state initializer. State
    // survives StrictMode's dev mount→unmount→remount (and HMR), so URLs made in
    // the initializer would be revoked by the first cleanup and never recreated,
    // leaving every row with a broken image.
    const [previews, setPreviews] = useState({});
    useEffect(() => {
        const map = Object.fromEntries(items.map(it => [it.id, URL.createObjectURL(it.file)]));
        setPreviews(map);
        return () => Object.values(map).forEach(u => URL.revokeObjectURL(u));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setItem = (id, patch) =>
        setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));

    const claimSlug = (nameEn, random = false) => {
        const base = slugify(nameEn) || `template-${Date.now()}`;
        let candidate = random ? `${base}-${uid().slice(0, 4)}` : base;
        for (let n = 2; usedSlugsRef.current.has(candidate); n += 1) candidate = `${base}-${n}`;
        usedSlugsRef.current.add(candidate);
        return candidate;
    };

    const buildFormData = (data, slug, categoryId, imageFile, genders) => {
        const fd = new FormData();
        fd.append('name', (data.nameEn || '').trim());
        fd.append('nameBn', (data.nameBn || '').trim());
        fd.append('slug', slug);
        fd.append('categoryId', String(categoryId));
        fd.append('prompt', typeof data.prompt === 'string' ? data.prompt.trim() : JSON.stringify(data.prompt));
        fd.append('negativePrompt', (data.negativePrompt || '').trim());
        fd.append('status', 'draft');
        fd.append('sortOrder', '0');
        fd.append('isFree', 'false');
        fd.append('isGlobal', 'true');
        fd.append('photoSize', 'jersey_full');
        fd.append('fields', JSON.stringify(mapFields(data.fields)));
        fd.append('tags', JSON.stringify(Array.isArray(data.tags) ? data.tags.filter(Boolean) : []));
        fd.append('genders', JSON.stringify(Array.isArray(genders) ? genders.filter(Boolean) : []));
        fd.append('watermarkEnabled', 'true');
        fd.append('watermarkX', '80');
        fd.append('watermarkY', '88');
        fd.append('requireUserImage', 'true');
        fd.append('imageSize', VALID_IMAGE_SIZES.includes(data.imageSize) ? data.imageSize : 'portrait_2x3');
        fd.append('allowMultipleImages', 'false');
        fd.append('requiredImageCount', '1');
        fd.append('imageLabels', JSON.stringify([{ label: '', required: true }]));
        // The uploaded example doubles as thumbnail AND raw analysis image, so
        // enhance/regenerate keep working when the draft is opened for review.
        fd.append('thumbnail', imageFile);
        fd.append('rawImage', imageFile);
        return fd;
    };

    const processItem = async (item) => {
        try {
            setItem(item.id, { status: 'analyzing', error: null });
            const optimized = await optimizeImage(item.file, { targetBytes: 490_000 }).catch(() => item.file);
            const genders = Array.isArray(item.genders) ? item.genders : [];
            const result = await aiTemplateService.analyzeImage(
                [optimized], categories, (item.note || '').trim(), '', genders, 0,
                Array.isArray(item.fieldTypes) ? item.fieldTypes : [],
            );
            const data = result?.data || result;
            if (!data?.nameEn || !data?.prompt) throw new Error(t('aiTemplateForm.bulkIncomplete'));

            const matched = categories.find(c => c.slug === data.categorySlug);
            const categoryId = matched?.id ?? categories[0]?.id;
            if (!categoryId) throw new Error(t('aiTemplateForm.bulkNoCategory'));

            setItem(item.id, { status: 'saving', templateName: data.nameEn });
            let created;
            try {
                created = await aiTemplateService.create(buildFormData(data, claimSlug(data.nameEn), categoryId, optimized, genders));
            } catch {
                // Most likely a slug collision with an EXISTING template — one
                // silent retry with a random suffix before giving up.
                created = await aiTemplateService.create(buildFormData(data, claimSlug(data.nameEn, true), categoryId, optimized, genders));
            }
            setItem(item.id, { status: 'done', createdId: created?.data?.id ?? null });
            // Invalidate the gallery cache so new drafts show up immediately.
            useAiTemplateCacheStore.getState().clear();
        } catch (err) {
            const message = isAiCapacityError(err)
                ? t('aiTemplateForm.analysisUnavailable')
                : (err?.message || t('aiTemplateForm.couldNotAnalyze'));
            setItem(item.id, { status: 'failed', error: message });
        }
    };

    // Kick off the batch, CONCURRENCY items at a time, with the options the
    // admin just configured. `items` here is the click-time state — current.
    const startRun = () => {
        if (startedRef.current) return;
        startedRef.current = true;
        setPhase('run');
        const queue = [...items];
        const worker = async () => {
            let it;
            while ((it = queue.shift())) await processItem(it); // eslint-disable-line no-cond-assign
        };
        for (let i = 0; i < CONCURRENCY; i += 1) worker();
    };

    const running = items.some(it => it.status === 'analyzing' || it.status === 'saving');
    const doneCount = items.filter(it => it.status === 'done').length;
    const failCount = items.filter(it => it.status === 'failed').length;
    const settled = doneCount + failCount;
    const finished = phase === 'run' && settled === items.length;

    // Warn before closing the tab while templates are still being created.
    useEffect(() => {
        if (!running) return;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [running]);

    // ── Phase 1: per-image options with Prev / Next ────────────────────────
    if (phase === 'configure') {
        const cur = items[cfgIndex];
        const isLast = cfgIndex === items.length - 1;
        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5">
                    {/* Header */}
                    <div className="flex items-start gap-2">
                        <button
                            type="button"
                            onClick={onBack}
                            className="mt-0.5 p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                        >
                            <IconArrowLeft size={18} />
                        </button>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
                                {t('aiTemplateForm.bulkConfigureTitle')}
                            </h2>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('aiTemplateForm.bulkConfigureSub')}
                            </p>
                        </div>
                        <span className="text-xs font-semibold text-primary flex-shrink-0 mt-1">
                            {t('aiTemplateForm.bulkImageOf', { n: cfgIndex + 1, total: items.length })}
                        </span>
                    </div>

                    {/* Image + options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mt-4">
                        <div className="min-w-0">
                            <img
                                src={previews[cur.id]}
                                alt=""
                                className="w-full h-auto max-h-[420px] object-contain rounded-lg bg-zinc-100 dark:bg-zinc-800"
                            />
                            <p className="mt-1.5 text-xs text-zinc-400 dark:text-zinc-500 truncate">{cur.file.name}</p>
                        </div>
                        <div className="min-w-0">
                            {/* Note */}
                            <label className="block text-xs font-medium mb-1 text-zinc-600 dark:text-zinc-400">
                                {t('aiTemplateForm.analysisNoteLabel')}
                                <span className="ml-1 font-normal text-zinc-400">({t('aiTemplateForm.analysisNoteOptional')})</span>
                            </label>
                            <textarea
                                rows={3}
                                value={cur.note}
                                onChange={e => setItem(cur.id, { note: e.target.value })}
                                placeholder={t('aiTemplateForm.analysisNotePlaceholder')}
                                className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                            />

                            {/* Genders */}
                            <label className="block text-xs font-medium mb-1.5 mt-3 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.genderLabel')}</label>
                            <div className="flex flex-wrap gap-1.5">
                                {GENDER_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setItem(cur.id, {
                                            genders: cur.genders.includes(opt.value)
                                                ? cur.genders.filter(v => v !== opt.value)
                                                : [...cur.genders, opt.value],
                                        })}
                                        className={chipCls(cur.genders.includes(opt.value))}
                                    >
                                        {t(opt.i18n)}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.genderHint')}</p>

                            {/* Field types */}
                            <label className="block text-xs font-medium mb-1.5 mt-3 text-zinc-700 dark:text-zinc-300">{t('aiTemplateForm.fieldTypesLabel')}</label>
                            <div className="flex flex-wrap gap-1.5">
                                {ANALYZE_FIELD_TYPE_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setItem(cur.id, {
                                            fieldTypes: cur.fieldTypes.includes(opt.value)
                                                ? cur.fieldTypes.filter(v => v !== opt.value)
                                                : [...cur.fieldTypes, opt.value],
                                        })}
                                        className={chipCls(cur.fieldTypes.includes(opt.value))}
                                    >
                                        {t(opt.i18n)}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t('aiTemplateForm.fieldTypesHint')}</p>
                        </div>
                    </div>

                    {/* Prev / dots / Next — options are optional, Next skips freely */}
                    <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => setCfgIndex(i => Math.max(0, i - 1))}
                            disabled={cfgIndex === 0}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-zinc-200 dark:border-zinc-700 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                        >
                            <IconChevronLeft size={15} /> {t('common.previous')}
                        </button>
                        <div className="flex items-center gap-1.5">
                            {items.map((it, i) => (
                                <button
                                    key={it.id}
                                    type="button"
                                    onClick={() => setCfgIndex(i)}
                                    aria-label={String(i + 1)}
                                    className={`w-2 h-2 rounded-full transition-colors ${i === cfgIndex
                                        ? 'bg-primary'
                                        : (it.note || it.genders.length || it.fieldTypes.length)
                                            ? 'bg-primary/40'
                                            : 'bg-zinc-200 dark:bg-zinc-700'}`}
                                />
                            ))}
                        </div>
                        {isLast ? (
                            <button
                                type="button"
                                onClick={startRun}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                            >
                                <IconSparkles size={15} /> {t('aiTemplateForm.bulkStartRun')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setCfgIndex(i => Math.min(items.length - 1, i + 1))}
                                className="inline-flex items-center gap-1 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                            >
                                {t('common.next')} <IconChevronRight size={15} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Phase 2: processing / results ──────────────────────────────────────
    const STATUS_UI = {
        waiting:   { cls: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400', icon: <IconClock size={13} />, label: t('aiTemplateForm.bulkStatusWaiting') },
        analyzing: { cls: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400', icon: <Spinner />, label: t('aiTemplateForm.bulkStatusAnalyzing') },
        saving:    { cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400', icon: <Spinner />, label: t('aiTemplateForm.bulkStatusSaving') },
        done:      { cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400', icon: <IconCheck size={13} />, label: t('aiTemplateForm.bulkStatusDone') },
        failed:    { cls: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400', icon: <IconX size={13} />, label: t('aiTemplateForm.bulkStatusFailed') },
    };

    return (
        <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5">
                {/* Header */}
                <div className="flex items-start gap-2 mb-1">
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={running}
                        className="mt-0.5 p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-primary hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:pointer-events-none transition-colors flex-shrink-0"
                    >
                        <IconArrowLeft size={18} />
                    </button>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
                            {t('aiTemplateForm.bulkTitle')}
                        </h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">
                            {t('aiTemplateForm.bulkSubtitle')}
                        </p>
                    </div>
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-1">
                        {t('aiTemplateForm.bulkProgress', { done: settled, total: items.length })}
                    </span>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden my-4">
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${(settled / items.length) * 100}%` }}
                    />
                </div>

                {/* Image rows */}
                <div className="space-y-2">
                    {items.map(it => {
                        const ui = STATUS_UI[it.status];
                        return (
                            <div key={it.id} className="flex items-center gap-3 rounded-lg border border-zinc-100 dark:border-zinc-800 p-2">
                                <img src={previews[it.id]} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0 bg-zinc-100 dark:bg-zinc-800" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100 truncate">
                                        {it.templateName || it.file.name}
                                    </p>
                                    {it.status === 'failed' && (
                                        <p className="text-xs text-red-500 truncate" title={it.error}>{it.error}</p>
                                    )}
                                </div>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold flex-shrink-0 ${ui.cls}`}>
                                    {ui.icon} {ui.label}
                                </span>
                                {it.status === 'done' && it.createdId && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(`/admin/ai-templates/${it.createdId}/edit`, '_blank', 'noopener')}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex-shrink-0 transition-colors"
                                    >
                                        <IconPencil size={13} /> {t('common.edit')}
                                    </button>
                                )}
                                {it.status === 'failed' && (
                                    <button
                                        type="button"
                                        onClick={() => processItem(it)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex-shrink-0 transition-colors"
                                    >
                                        <IconRefresh size={13} /> {t('aiTemplateForm.bulkRetry')}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Summary + review CTA once every image has settled */}
                {finished && (
                    <div className="mt-4 pt-4 border-t border-zinc-100 dark:border-zinc-800 flex flex-col sm:flex-row sm:items-center gap-3">
                        <p className="flex-1 text-sm text-zinc-600 dark:text-zinc-400">
                            {t('aiTemplateForm.bulkSummary', { ok: doneCount, fail: failCount })}
                        </p>
                        {doneCount > 0 && (
                            <button
                                type="button"
                                onClick={() => navigate('/admin/ai-templates?status=draft')}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                            >
                                <IconPencil size={15} /> {t('aiTemplateForm.bulkReviewDrafts')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Spinner() {
    return <span className="w-3 h-3 border-[1.5px] border-current border-t-transparent rounded-full animate-spin" />;
}
